/* eslint-disable no-nested-ternary */
import GdsClient, { EventResponse, GuestWeddingResponse } from '../../gds-api-client';
import Icon from '@xo-union/tk-component-icons';
import { H4, Body1 } from '@xo-union/tk-ui-typography';
import { InlineAlert } from '@xo-union/tk-component-alerts';
import { ScheduleOfEventsModal } from '../../component-event-modal';
import { get } from 'lodash';
import React, { useMemo, useReducer } from 'react';

import { ChangeEventsState, EventCount, EventType, Question } from '../EventTypes';
import { Household } from '../HouseholdTypes';
import { GuestWedding } from '../GuestWeddingTypes';
import styles from './rsvpConfiguration.scss';
import EventModule from './EventModule';
import UnpublishedWWSError from './UnpublishedWWSErrorMessage';
import RsvpHiddenMessage from './RsvpHiddenMessage';
import GeneralQuestionModule from './GeneralQuestionModule';
import QuestionRightRail from './QuestionRightRail';
import RsvpRightRail from '../RSVPSettingsRightRail';
import Toast from './Toast';
import { track } from '../helpers/analytics';

export interface RsvpConfigurationProps {
  isPrivateRsvp: boolean;
  wwsState: string;
  events: EventType[];
  households: Household[];
  guestWedding: GuestWedding;
  variant?: string;
  onCancelClick: (variant: string) => void;
  onSuccess: (data: GuestWeddingResponse, variant?: string) => void;
  onEventsUpdate: (events: EventResponse[], variant?: string) => void;
  goToGuestList: (id: string) => void;
  isExperiment?: boolean;
}

export interface Errors {
  [key: string]: {
    error: string;
  };
}

export type GuestWeddingProperty = 'isPrivateRsvp' | 'allowGuestPreview' | 'rsvpPageHidden';

interface State {
  eventUpdates: ChangeEventsState;
  questionRailOpen: boolean;
  rsvpRailOpen: boolean;
  eventRailOpen: boolean;
  questionRailQuestion: Question | null;
  questionRailEventId: string | null;
  questionRailGuestWedding: GuestWeddingResponse | null;
  errors: Errors;
}

interface QuestionRailAction {
  open: boolean;
  event: EventType | null;
  question: Question | null;
}

type Actions =
  | { type: 'SET_QUESTION_RAIL_OPEN'; payload: QuestionRailAction }
  | { type: 'SET_ERRORS'; payload: Errors }
  | { type: 'SET_RSVP_RAIL_OPEN'; payload: boolean }
  | { type: 'SET_EVENT_RAIL_OPEN'; payload: boolean };

function reducer(state: State, action: Actions): State {
  switch (action.type) {
    case 'SET_QUESTION_RAIL_OPEN':
      return {
        ...state,
        questionRailOpen: action.payload.open,
        questionRailEventId: get(action, ['payload', 'event', 'id']),
        questionRailQuestion: action.payload.question,
      };

    case 'SET_RSVP_RAIL_OPEN':
      return {
        ...state,
        rsvpRailOpen: action.payload,
      };

    case 'SET_EVENT_RAIL_OPEN':
      return {
        ...state,
        eventRailOpen: action.payload,
      };

    case 'SET_ERRORS':
      return { ...state, errors: action.payload };

    default:
      throw new Error('Type is not defined');
  }
}

function guestCountByEvent(households: Household[]): EventCount {
  // Get count of guests by event ID
  const guestsByEvent: any = {};
  if (households?.length) {
    households.forEach(h => {
      if (h.people) {
        h.people.forEach(p => {
          if (p.invitations && p.invitations.length > 0) {
            p.invitations.forEach(i => {
              guestsByEvent[i.eventId] = guestsByEvent[i.eventId] ? guestsByEvent[i.eventId] + 1 : 1;
            });
          }
        });
      }
    });
  }
  return guestsByEvent;
}

function householdCountByEvent(households: Household[]): EventCount {
  // Get count of households by event ID
  const householdsByEvents: any = {};
  if (households?.length) {
    households.forEach(h => {
      if (h.people) {
        h.people.forEach(p => {
          if (p.invitations) {
            p.invitations.forEach(i => {
              if (!householdsByEvents[i.eventId]) {
                householdsByEvents[i.eventId] = new Set();
              }
              householdsByEvents[i.eventId].add(h.id);
            });
          }
        });
      }
    });
  }

  const final: any = {};
  Object.keys(householdsByEvents).forEach(eventId => {
    final[eventId] = householdsByEvents[eventId].size;
  });
  return final;
}

// returns the number of answers for each event's custom questions
function answerCountByEvent(households: Household[]): EventCount {
  const answersByEvent: any = {};
  if (households?.length) {
    households.forEach(h => {
      if (h.people) {
        h.people.forEach(p => {
          if (p.invitations) {
            p.invitations.forEach(i => {
              if (i.answers) {
                if (!answersByEvent[i.eventId]) {
                  answersByEvent[i.eventId] = new Set();
                }
                answersByEvent[i.eventId].add(p.id);
              }
            });
          }
        });
      }
    });
  }

  const final: any = {};
  Object.keys(answersByEvent).forEach(eventId => {
    final[eventId] = answersByEvent[eventId].size;
  });
  return final;
}

function getRsvpOnCount(events: EventType[]): number {
  let count = 0;
  events.forEach(event => {
    if (event.requireRsvp) count += 1;
  });

  return count;
}

function getHasPrivateEvent(events: EventType[]): boolean {
  return events.some(e => e.visible === false);
}

const ERROR_MESSAGE = 'Oops! Something went wrong. Please try again later.';

// Instantiate the client outside the component so it doesn't get recrated on each re-render
const client = new GdsClient();

const RsvpConfiguration: React.FC<RsvpConfigurationProps> = ({
  isPrivateRsvp,
  events,
  households,
  guestWedding,
  onSuccess,
  onCancelClick,
  onEventsUpdate,
  wwsState,
  goToGuestList,
  variant = 'New RSVP Settings',
  isExperiment,
}) => {
  const [
    { errors, questionRailOpen, rsvpRailOpen, eventRailOpen, questionRailEventId, questionRailQuestion },
    dispatch,
  ] = useReducer<(state: State, action: Actions) => State>(reducer, {
    eventUpdates: {},
    errors: {},
    questionRailOpen: false,
    rsvpRailOpen: false,
    eventRailOpen: false,
    questionRailQuestion: null,
    questionRailEventId: null,
    questionRailGuestWedding: null,
  });

  const cancel = (): void => onCancelClick(variant);

  const updateGuestWedding = async (updatedProperty: GuestWeddingProperty, payload: boolean): Promise<void> => {
    // Save change to GDS
    try {
      const response = await client.updateGuestWedding({ [updatedProperty]: payload });

      // Clear errors if this was successful
      dispatch({ type: 'SET_ERRORS', payload: {} });

      onSuccess(response.data, variant);
    } catch ({ response }) {
      if (get(response, ['data', 'error'])) {
        dispatch({ type: 'SET_ERRORS', payload: { unknown: { error: ERROR_MESSAGE } } });
      }
    }
  };

  const openQuestionRail = (question: Question | null, event: EventType | null): void => {
    dispatch({
      type: 'SET_QUESTION_RAIL_OPEN',
      payload: { open: true, event, question },
    });
  };

  const closeRsvpRail = (): void =>
    dispatch({
      type: 'SET_RSVP_RAIL_OPEN',
      payload: false,
    });

  const closeEventRail = (): void => {
    dispatch({
      type: 'SET_EVENT_RAIL_OPEN',
      payload: false,
    });
  };

  const closeQuestionRail = (): void =>
    dispatch({
      type: 'SET_QUESTION_RAIL_OPEN',
      payload: { open: false, event: null, question: null },
    });

  const guestsByEvent = useMemo(() => guestCountByEvent(households), [households]);
  const householdsByEvent = useMemo(() => householdCountByEvent(households), [households]);
  const answersByEvent = useMemo(() => answerCountByEvent(households), [households]);
  const rsvpOnCount = useMemo(() => getRsvpOnCount(events), [events]);
  const hasPrivateEvent = useMemo(() => getHasPrivateEvent(events), [events]);

  const questionRailEvent = useMemo(() => events.find(e => e.id === questionRailEventId) || null, [
    events,
    questionRailEventId,
  ]);

  const { rsvpPageHidden = false } = guestWedding;
  const wwsPublished = wwsState === 'published';
  const rsvpAbleEvents = useMemo(() => events.filter(event => event.requireRsvp), [events]) || [];
  const showRequireRsvpMessage = useMemo(() => !rsvpAbleEvents.length, [rsvpAbleEvents]);

  const eventList = events.map((evt, idx) => {
    // Add guest count to event
    // GLM may already provide the guestCount property
    // only set it if it does not exist.
    const e = { ...evt };
    if (e.guestCount === undefined || e.guestCount === 0) {
      e.guestCount = guestsByEvent[e.id] ? guestsByEvent[e.id] : 0;
    }
    if (e.householdCount === undefined || e.guestCount === 0) {
      e.householdCount = householdsByEvent[e.id] ? householdsByEvent[e.id] : 0;
    }

    return (
      <EventModule
        key={e.id}
        rootTestId={idx}
        onEventsUpdate={onEventsUpdate}
        displayRsvpToggle={wwsPublished}
        event={e}
        goToGuestList={goToGuestList}
        privateRsvp={isPrivateRsvp}
        rsvpOnCount={rsvpOnCount}
        openQuestionRail={openQuestionRail}
        gdsClient={client}
        numOfRsvpAbleEvents={rsvpAbleEvents.length}
        numOfAnswers={answersByEvent[e.id] || null}
        updateGuestWedding={updateGuestWedding}
      />
    );
  });

  return (
    <>
      <div className={styles.titleContainer}>
        <div className={styles.backContainer}>
          <Icon name="back" onClick={cancel} size="md" data-testid="back-arrow" />
        </div>
        <H4 className={styles.heading}>Online RSVP</H4>
      </div>
      <div className={styles.rsvpConfigurationContainer}>
        {errors && errors.unknown && <Toast show type="Error" message={errors.unknown.error} />}
        {!wwsPublished ? (
          <UnpublishedWWSError />
        ) : showRequireRsvpMessage ? (
          <InlineAlert type="warning">
            Heads up! To use Online RSVP, you must have ‘Collect RSVPs’ on for at least one event.
          </InlineAlert>
        ) : rsvpAbleEvents.length ? (
          <RsvpHiddenMessage
            rsvpPageHidden={rsvpPageHidden}
            isPrivateRsvp={isPrivateRsvp}
            onClick={(): void =>
              dispatch({
                type: 'SET_RSVP_RAIL_OPEN',
                payload: true,
              })
            }
          />
        ) : null}
        {eventList}
        <GeneralQuestionModule guestWedding={guestWedding} openQuestionRail={openQuestionRail} />
        <button
          type="button"
          className={styles.addEvent}
          onClick={(): void => {
            track('RSVP Settings Interaction', {
              selection: 'add another event initiated',
            });
            dispatch({
              type: 'SET_EVENT_RAIL_OPEN',
              payload: true,
            });
          }}
          data-testid="addAnotherEvent--configuration"
        >
          <Body1>+ Add Another Event</Body1>
        </button>
      </div>
      <QuestionRightRail
        isOpen={questionRailOpen}
        event={questionRailEvent}
        guestWedding={guestWedding}
        households={households}
        question={questionRailQuestion}
        onEventsUpdate={onEventsUpdate}
        onSuccess={onSuccess}
        onClose={closeQuestionRail}
        onEscapeKey={closeQuestionRail}
      />
      <RsvpRightRail
        showRail={rsvpRailOpen}
        rsvpOnInEvents={!!rsvpAbleEvents?.length}
        hasPrivateEvent={hasPrivateEvent}
        wwsState={wwsState}
        guestWedding={guestWedding}
        onClickEditForm={closeRsvpRail}
        onClose={closeRsvpRail}
        onEscapeKey={closeRsvpRail}
        onSuccess={(data: GuestWeddingResponse): void => {
          onSuccess(data);
          closeRsvpRail();
        }}
      />
      <ScheduleOfEventsModal
        open={eventRailOpen}
        type="WeddingEvent"
        event={null}
        weddingId={guestWedding.id}
        setClose={closeEventRail}
        onEscapeKey={closeEventRail}
        hasWWS={wwsState === 'published'}
        onSuccess={(e: EventResponse): void => {
          onEventsUpdate([e]);
        }}
        onHideDate={(): void => {}}
        baseAnalyticsProps={{ source: 'rsvp settings' }}
        isExperiment={isExperiment}
      />
    </>
  );
};

export default RsvpConfiguration;
